<template>
  <div
    class="mf-snackbar"
    :class="[`mf-snackbar--${variant}`, { 'mf-snackbar--full': size === 'full-width' }]"
    :role="role"
    :aria-live="ariaLive"
    aria-atomic="true"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <!-- leading-icon: instância do _Icons set (Governança 10 do Outline), categoria Status/Alert,
         Size=md (20px). Nunca um path próprio: o glifo e o tamanho pertencem ao átomo Icon. -->
    <MfIcon v-if="showIcon" class="mf-snackbar__icon" :name="iconName" size="md" />

    <div class="mf-snackbar__content">
      <p class="mf-snackbar__message">
        <slot>{{ message }}</slot>
      </p>
      <!-- action-label: link de texto sublinhado, NÃO um Button (Governança 5 do Outline). -->
      <button v-if="showAction && actionLabel" type="button" class="mf-snackbar__action" @click="onAction">{{ actionLabel }}</button>
    </div>

    <button type="button" class="mf-snackbar__close" aria-label="Fechar" @click="$emit('dismiss')">
      <MfIcon name="close" size="md" />
    </button>
  </div>
</template>

<script>
import MfIcon from '../MfIcon/MfIcon.vue'

/**
 * Snackbar item (Molecule): feedback transitório e não-bloqueante do resultado de uma ação,
 * sobreposto à interface e com auto-dismiss. Difere do Alert (inline e persistente). Neutral usa
 * surface/inverse (escuro). Sem borda em nenhuma variante. Comportamento (timer, hover-pausa) é
 * contrato de código, não do Figma. Tokens em gondola.tokens.css.
 * Figma: `Snackbar item` 970:56 (40 variantes). Outline "Snackbar" v1.0.0.
 *
 * ÍCONE POR VARIANTE (lido variante por variante no Figma, não inferido):
 * neutral 969:24 e info 969:206 usam `Status/Alert, info` (812:81); success 969:56 usa
 * `Status/Alert, check` (812:133); error 969:124 usa `Status/Alert, error` (812:139);
 * warning 969:174 usa `Status/Alert, warning` (812:127). Todos na variante contornada (não
 * `-filled`) e em Size=md. A cor vem do consumidor via currentColor.
 *
 * ELEVAÇÃO: o Outline lista o effect style `elevation/tooltip` no container, mas nenhuma das 5
 * variantes lidas no Figma (969:24, 969:15, 969:56, 969:124, 969:174, 969:206) declara effect
 * style algum, e a API de variáveis não expõe effect style. Sem valor de fonte, este componente
 * NÃO desenha sombra: o valor `0 4px 12px rgba(0,0,0,.18)` que estava aqui não tinha origem.
 * Divergência aberta para o design decidir (ver MfSnackbar.mdx, Governança).
 */
export default {
  name: 'MfSnackbar',
  components: { MfIcon },
  props: {
    /** Estado semântico. Define cor, ícone e urgência (role/aria-live). */
    variant: {
      type: String,
      default: 'neutral',
      validator: (value) => ['neutral', 'success', 'error', 'warning', 'info'].includes(value),
    },
    /** Mensagem (alternativa ao slot default). Mantenha curta. */
    message: { type: String, default: '' },
    /** Rótulo da ação (aparece com showAction). Ativar dispara `action` e fecha. */
    actionLabel: { type: String, default: '' },
    /** Exibe o ícone de status (recomendado: canal redundante à cor). */
    showIcon: { type: Boolean, default: true },
    /** Exibe o link de ação. */
    showAction: { type: Boolean, default: false },
    /** Largura: default (min 320px) ou full-width (preenche o container). */
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'full-width'].includes(value),
    },
    /**
     * Duração do auto-dismiss em ms. `null` = automático (error não some; com ação 6000; senão 4000).
     * `0` = persiste (sem auto-dismiss).
     */
    duration: { type: Number, default: null },
  },
  emits: ['dismiss', 'action'],
  data() {
    // sem prefixo `_`: Vue reserva `_`/`$` em data (vue/no-reserved-keys)
    return { remainingMs: 0, timerId: null, startedAt: 0 }
  },
  computed: {
    /** Nome no catálogo do _Icons set, por variante. Conferido node a node no Figma. */
    iconName() {
      return {
        neutral: 'info',
        success: 'check',
        error: 'error',
        warning: 'warning',
        info: 'info',
      }[this.variant]
    },
    role() {
      return this.variant === 'error' || this.variant === 'warning' ? 'alert' : 'status'
    },
    ariaLive() {
      return this.role === 'alert' ? 'assertive' : 'polite'
    },
    effectiveDuration() {
      if (this.duration != null) return this.duration
      if (this.variant === 'error') return 0
      return this.showAction ? 6000 : 4000
    },
  },
  mounted() {
    this.remainingMs = this.effectiveDuration
    this.resume()
  },
  beforeUnmount() {
    this.clearTimer()
  },
  methods: {
    clearTimer() {
      if (this.timerId) {
        clearTimeout(this.timerId)
        this.timerId = null
      }
    },
    resume() {
      if (this.remainingMs > 0 && !this.timerId) {
        this.startedAt = Date.now()
        this.timerId = setTimeout(() => this.$emit('dismiss'), this.remainingMs)
      }
    },
    pause() {
      if (this.timerId) {
        this.remainingMs -= Date.now() - this.startedAt
        this.clearTimer()
      }
    },
    onAction() {
      this.$emit('action')
      this.$emit('dismiss')
    },
  },
}
</script>

<style scoped>
.mf-snackbar {
  display: flex;
  align-items: flex-start;
  gap: var(--mf-snackbar-gap);
  box-sizing: border-box;
  /* 320px é literal de propósito: Governança 8 do Outline crava o min-width como constraint nativo
     de layout e proíbe `dimension/320` para não poluir a escala de geometria. */
  min-width: 320px;
  padding: var(--mf-snackbar-padding-y) var(--mf-snackbar-padding-x);
  border-radius: var(--mf-snackbar-radius);
  background: var(--_snk-surface);
  color: var(--_snk-content);
  font-family: var(--mf-font-family);
}
.mf-snackbar--full {
  width: 100%;
  min-width: 0;
}
.mf-snackbar--neutral {
  --_snk-surface: var(--mf-snackbar-neutral-surface);
  --_snk-content: var(--mf-snackbar-neutral-content);
  --_snk-action: var(--mf-snackbar-neutral-action);
}
.mf-snackbar--success {
  --_snk-surface: var(--mf-snackbar-success-surface);
  --_snk-content: var(--mf-snackbar-success-content);
  --_snk-action: var(--mf-snackbar-success-action);
}
.mf-snackbar--error {
  --_snk-surface: var(--mf-snackbar-error-surface);
  --_snk-content: var(--mf-snackbar-error-content);
  --_snk-action: var(--mf-snackbar-error-action);
}
.mf-snackbar--warning {
  --_snk-surface: var(--mf-snackbar-warning-surface);
  --_snk-content: var(--mf-snackbar-warning-content);
  --_snk-action: var(--mf-snackbar-warning-action);
}
.mf-snackbar--info {
  --_snk-surface: var(--mf-snackbar-info-surface);
  --_snk-content: var(--mf-snackbar-info-content);
  --_snk-action: var(--mf-snackbar-info-action);
}

/* leading-icon não tem estilo próprio: tamanho e cor vivem no MfIcon (size=md) e no currentColor
   herdado do container. A classe existe como hook de anatomia e de teste. */
.mf-snackbar__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--mf-snackbar-content-gap);
  flex: 1 1 0;
  min-width: 0;
}
.mf-snackbar__message {
  margin: 0;
  font-size: var(--mf-typography-body-small-size);
  line-height: var(--mf-typography-body-small-lh);
  font-weight: var(--mf-font-weight-regular);
  overflow-wrap: break-word;
}
/* Label/Medium + underline. Peso `medium`, nunca bold: Governança 5 do Outline. */
.mf-snackbar__action {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--_snk-action);
  font-family: inherit;
  font-size: var(--mf-typography-label-medium-size);
  line-height: var(--mf-typography-label-medium-lh);
  font-weight: var(--mf-font-weight-medium);
  text-decoration: underline;
  cursor: pointer;
}
.mf-snackbar__action:focus-visible {
  outline: var(--mf-stroke-width-2) solid currentColor;
  outline-offset: var(--mf-dimension-02);
}
.mf-snackbar__close {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--mf-snackbar-close-size);
  height: var(--mf-snackbar-close-size);
  padding: 0;
  border: 0;
  border-radius: var(--mf-radius-sm);
  background: transparent;
  color: inherit;
  cursor: pointer;
}
/* WCAG 2.5.5: o quadro visível fica em 32px (é ele que dá os 56px de altura da linha no Figma) e a
   área de toque cresce para 44px por área invisível. O inset negativo sai da conta entre os dois
   tokens, sem número mágico. Outline "Snackbar" v1.0.0, seção Accessibility. */
.mf-snackbar__close::after {
  content: '';
  position: absolute;
  inset: calc((var(--mf-snackbar-close-size) - var(--mf-snackbar-close-touch-target)) / 2);
}
.mf-snackbar__close:focus-visible {
  outline: var(--mf-stroke-width-2) solid currentColor;
  outline-offset: var(--mf-dimension-02);
}
</style>
